<template>
  <v-card
    flat
    class="pa-4 rounded-lg text-start"
    :class="questionAnswer.hasInvalidChoices ? 'error-border' : ''"
  >
    <!--questionAnswers question-->
    <v-row no-gutters class="mb-6">
      <v-col>
        <LocalisedContentEditable
          :values="questionAnswer.question"
          @update:values="updateQuestion"
          :text-area-rows="5"
          :placeholder="$t('course.courseDetails.question')"
          :editable="isEditing"
          css-class="body-1 font-weight-regular textLight--text"
          :hasError="questionAnswer.hasInvalidQuestion"
          :selectedLocale="selectedLocale"
        />
      </v-col>

      <v-col v-if="isDraggable" cols="auto">
        <v-icon color="primaryDark" class="question-handle pb-3 drag-cursor">
          mdi-drag-variant
        </v-icon>
      </v-col>
    </v-row>

    <!--questionAnswers choices-->
    <template>
      <v-row
        v-for="(choice, choiceIndex) in questionAnswer.choices"
        :key="`choice_${choiceIndex}`"
        no-gutters
        class="mb-4"
        :class="!isEditing ? 'px-2' : ''"
      >
        <v-col>
          <LocalisedContentEditable
            :values="choice.value"
            @update:values="values => updateChoiceValue(values, choiceIndex)"
            :prefix="`${choiceIndex + 1}. `"
            :placeholder="`${$t('course.courseDetails.option')} ${
              choiceIndex + 1
            }`"
            :editable="isEditing"
            css-class="body-1 font-weight-light textLight--text"
            :hasError="choice.hasInvalidChoice"
            :selectedLocale="selectedLocale"
          />
        </v-col>
      </v-row>
    </template>

    <!--questionAnswers correct answer & mark-->
    <template>
      <!--questionAnswers correct answer-->
      <v-row class="mt-10 mb-4">
        <v-col cols="12" lg="auto" class="pb-0">
          <span class="body-1 font-weight-regular textLight--text">
            {{ $t("course.courseDetails.correctAnswer") }}
          </span>
        </v-col>

        <v-col
          v-for="(choice, choiceIndex) in questionAnswer.choices"
          :key="`choice_button_${choiceIndex}`"
          cols="6"
          sm="3"
          lg=""
          class=""
        >
          <v-btn
            depressed
            small
            block
            min-width="80"
            class="text-capitalize rounded-2"
            :class="
              choice.key === questionAnswer.answerKey
                ? 'primary'
                : 'button-dark'
            "
            :style="!isEditing ? 'pointer-events: none;' : ''"
            @click="isEditing ? updateAnswerKey(choice.key) : null"
          >
            {{ `${$t("course.courseDetails.option")} ${choiceIndex + 1}` }}
          </v-btn>
        </v-col>
      </v-row>

      <!--questionAnswers mark-->
      <CourseNumberField
        :label="$t('course.courseDetails.questionMark')"
        :value="questionAnswer.mark"
        @update:value="updateQuestionMark"
        :isEditing="isEditing"
        css-class="body-1 font-weight-medium textLight--text"
        class="mb-4"
      />
    </template>

    <!--quizContent displayPosition-->
    <CourseNumberField
      v-if="showDisplayPosition"
      :label="$t('course.courseDetails.displayPosition')"
      :value="questionAnswer.displayPosition"
      @update:value="updateQuestionDisplayPosition"
      :isEditing="isEditing"
      css-class="body-1 font-weight-medium textLight--text"
      class="mb-4"
      :hasWarning="isDisplayPositionAlreadyUsed"
      :warningMessage="
        isDisplayPositionAlreadyUsed
          ? $t('course.courseDetails.displayOrderDuplicate')
          : ''
      "
    />

    <!--questionAnswers delete button-->
    <v-row v-if="isEditing" no-gutters justify="end" class="mt-3">
      <v-col cols="auto">
        <v-btn
          small
          depressed
          color="#D75B5B"
          min-width="80"
          class="rounded-1 ms-2 white--text"
          @click="$emit('onDelete')"
        >
          <v-icon small class="me-1">mdi-delete</v-icon>

          {{ $t("course.courseDetails.delete") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import LocalisedContentEditable from "@/app/core-module/components/LocalisedContentEditable";
import CourseNumberField from "@/app/course-module/components/course-details/CourseNumberField";

export default {
  name: "QuizQuestionAnswer",
  components: { CourseNumberField, LocalisedContentEditable },
  props: {
    questionAnswer: {
      type: Object,
      required: true,
    },
    existingDisplayPositions: {
      type: Array,
      default: () => [],
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    selectedLocale: {
      type: String,
      default: "en",
    },
    showDisplayPosition: {
      type: Boolean,
      default: false,
    },
    isDraggable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDisplayPositionAlreadyUsed() {
      return (
        this.existingDisplayPositions.length &&
        this.questionAnswer.displayPosition != null &&
        this.existingDisplayPositions.filter(
          x => x === this.questionAnswer.displayPosition
        )?.length > 1
      );
    },
  },
  methods: {
    updateQuestion(values) {
      const clonedQuestionAnswer = { ...this.questionAnswer, question: values };
      this.$emit("onUpdate", clonedQuestionAnswer);
    },
    updateChoiceValue(values, choiceIndex) {
      const clonedQuestionAnswer = { ...this.questionAnswer };

      clonedQuestionAnswer.choices[choiceIndex] = {
        ...clonedQuestionAnswer.choices[choiceIndex],
        value: values,
      };

      this.$emit("onUpdate", clonedQuestionAnswer);
    },
    updateAnswerKey(value) {
      const clonedQuestionAnswer = { ...this.questionAnswer, answerKey: value };
      this.$emit("onUpdate", clonedQuestionAnswer);
    },
    updateQuestionMark(value) {
      const clonedQuestionAnswer = {
        ...this.questionAnswer,
        mark: this.getInteger(value),
      };

      this.$emit("onUpdate", clonedQuestionAnswer);
    },
    updateQuestionDisplayPosition(value) {
      const clonedQuestionAnswer = {
        ...this.questionAnswer,
        displayPosition: this.getInteger(value),
      };

      this.$emit("onUpdate", clonedQuestionAnswer);
    },
  },
};
</script>

<style scoped></style>
